import { getAccessToken } from "./supabase";
import { ECUADOR_MAP_PACKS } from "../../shared/ecuador-map-packs";

type StoredMapPack = { file: string; blob: Blob; size: number; downloadedAt: string };
const DATABASE = "thera-offline-maps";
const STORE = "packs";

export function terrainFileFor(file: string) {
  // Todos los relieves nacionales usan el sufijo v3 para conservar la
  // corrección hidrográfica ya validada en Azuay y Cañar.
  return ECUADOR_MAP_PACKS.find((pack) => pack.file === file)?.terrainFile ?? null;
}

export function contourFileFor(file: string) {
  if (!file.endsWith("-detallado.pmtiles")) return null;
  return file.replace(/-detallado\.pmtiles$/, "-curvas-v1.pmtiles");
}

function mapsApiUrl(file: string) {
  const configuredBase = import.meta.env.VITE_MAPS_API_BASE?.replace(/\/$/, "");
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const protocol = typeof window !== "undefined" ? window.location.protocol : "";
  const localAppShell = protocol === "capacitor:" || hostname === "localhost" || hostname === "127.0.0.1";

  // Web/PWA: same-origin es la ruta más segura. Android/Capacitor: el shell
  // local no tiene /api, así que debe apuntar explícitamente a Cloudflare.
  if (!localAppShell && !configuredBase) return `/api/maps/${file}`;
  const base = configuredBase || "https://thera-rutas-ecuador.pages.dev";
  return `${base}/api/maps/${file}`;
}

export type RemoteMapInfo = {
  available: boolean;
  size: number;
};

export async function getRemoteMapInfo(file: string): Promise<RemoteMapInfo> {
  const token = await getAccessToken();
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  try {
    const response = await fetch(mapsApiUrl(file), { method: "HEAD", headers });
    return {
      available: response.ok,
      size: response.ok ? Number(response.headers.get("Content-Length") ?? 0) : 0,
    };
  } catch {
    return { available: false, size: 0 };
  }
}

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE)) database.createObjectStore(STORE, { keyPath: "file" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function savePack(pack: StoredMapPack) {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).put(pack);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

async function saveAndVerifyPack(pack: StoredMapPack) {
  try {
    await navigator.storage?.persist?.().catch(() => false);
    await savePack(pack);
    const stored = await getStoredMapPack(pack.file);
    return Boolean(stored
      && stored.size === pack.size
      && stored.blob instanceof Blob
      && stored.blob.size === pack.blob.size
      && stored.blob.size > 0);
  } catch {
    return false;
  }
}

export async function getStoredMapPack(file: string) {
  const database = await openDatabase();
  const result = await new Promise<StoredMapPack | undefined>((resolve, reject) => {
    const request = database.transaction(STORE, "readonly").objectStore(STORE).get(file);
    request.onsuccess = () => resolve(request.result as StoredMapPack | undefined);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result;
}

export async function hasStoredMapPack(file: string) {
  return Boolean(await getStoredMapPack(file));
}

export async function removeStoredMapPack(file: string) {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).delete(file);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function downloadMapPack(
  file: string,
  onProgress: (value: number) => void,
  options: { allowGuest?: boolean; persist?: boolean } = {},
) {
  const token = await getAccessToken();
  if (!token && !options.allowGuest) throw new Error("Inicia sesión con una cuenta aprobada para descargar mapas.");
  const headers = new Headers();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const response = await fetch(mapsApiUrl(file), { headers });
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(problem?.error ?? "No fue posible descargar el mapa regional.");
  }
  const expected = Number(response.headers.get("Content-Length") ?? 0);
  if (expected && navigator.storage?.estimate) {
    const { quota = 0, usage = 0 } = await navigator.storage.estimate();
    if (quota - usage < expected * 1.2) throw new Error("No existe espacio suficiente para guardar este mapa.");
  }
  const reader = response.body?.getReader();
  if (!reader) {
    const blob = await response.blob();
    const saved = options.persist === false
      ? false
      : await saveAndVerifyPack({ file, blob, size: blob.size, downloadedAt: new Date().toISOString() });
    onProgress(100);
    return { blob, saved };
  }
  const chunks: ArrayBuffer[] = [];
  let received = 0;
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const exact = value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength) as ArrayBuffer;
    chunks.push(exact);
    received += value.byteLength;
    onProgress(expected ? Math.min(98, Math.round((received / expected) * 100)) : Math.min(98, 10 + chunks.length));
  }
  const blob = new Blob(chunks, { type: "application/vnd.pmtiles" });
  const saved = options.persist === false
    ? false
    : await saveAndVerifyPack({ file, blob, size: blob.size, downloadedAt: new Date().toISOString() });
  onProgress(100);
  return { blob, saved };
}
